import type { CSSProperties } from 'react'
import { Box, Button, Card, Checkbox, Text } from '@grovus/design-system'
import type { DisclosurePanels } from '../components/useDisclosurePanel'
import { COLUMN_KEYS, PINNED_KEYS, columnDefs, type ColumnKey } from './columns'
import type { SortKey } from './partition'
import type { ViewContext } from './view'

export const CHOOSER_PANEL = 'columns'

interface Props {
  ctx: ViewContext
  hidden: ReadonlySet<ColumnKey>
  onToggle: (key: ColumnKey) => void
  onShowAll: () => void
  sort: SortKey
  dir: 'asc' | 'desc'
  panels: DisclosurePanels
}

/**
 * The gear that opens one checkbox per column (GRO-64, review remark 11).
 *
 * Every column is listed, the pinned two included: they show as checked and disabled with
 * a note saying why, so the reader sees the whole set rather than wondering where two went.
 * The column the table is sorted by carries the header's arrow, because a hidden sort
 * column still orders the rows and nothing else on screen would say so.
 */
export function ColumnChooser({ ctx, hidden, onToggle, onShowAll, sort, dir, panels }: Props) {
  const { t } = ctx
  const defs = columnDefs(t)
  const open = panels.isOpen(CHOOSER_PANEL)

  return (
    <div className="page-anchor">
      <Button
        variant="default"
        {...panels.triggerProps(CHOOSER_PANEL)}
        aria-label={t.colChooser}
        title={t.colChooser}
        data-testid="column-chooser-toggle"
      >
        {/* The text presentation selector keeps the gear a glyph in the ink colour rather
            than an emoji a platform paints in its own. */}
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>{'⚙︎'}</span>
      </Button>
      {open && (
        <Card
          role="group"
          aria-label={t.colChooser}
          className="page-panel"
          style={{ '--panel-width': '280px' } as CSSProperties}
          data-testid="column-chooser"
          {...panels.panelProps(CHOOSER_PANEL)}
        >
          {COLUMN_KEYS.map((key) => {
            const pinned = PINNED_KEYS.includes(key)
            const sorted = sort === key
            return (
              <Box key={key} py={4}>
                <Checkbox
                  checked={pinned || !hidden.has(key)}
                  disabled={pinned}
                  onChange={() => onToggle(key)}
                  data-testid={`column-toggle-${key}`}
                  label={(
                    <span>
                      {defs[key].header}
                      {pinned && (
                        <Text component="span" size="xs" className="grv-muted">{` · ${t.colAlwaysShown}`}</Text>
                      )}
                      {sorted && (
                        <Text component="span" size="xs" className="grv-muted" data-testid="column-sorted">
                          {` · ${dir === 'asc' ? '↑' : '↓'} ${t.colSorted}`}
                        </Text>
                      )}
                    </span>
                  )}
                />
              </Box>
            )
          })}
          <Box pt="sm" mt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Button
              variant="default"
              size="compact-sm"
              disabled={hidden.size === 0}
              onClick={onShowAll}
              data-testid="column-show-all"
            >
              {t.colShowAll}
            </Button>
          </Box>
        </Card>
      )}
    </div>
  )
}
